import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography, TextField, Stack, IconButton, FormControlLabel, Checkbox, Button } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, Add as AddIcon } from '@mui/icons-material';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  ExcelExport,
  PdfExport,
  FilterType,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids';

import { useGetAllContractsQuery } from '../../../../../../services/ContractService';
import { ContractView } from '../../../../../../services/ContractService/types/contractTypes';
import { PageLoading as Loading } from '../../../../../common/components';
import { useContractSearch, useContractNavigation } from './hooks';
import {
  payerTemplate,
  productTemplate,
  financialClassTemplate,
  calcTypeTemplate,
  facilityTypeTemplate,
} from './templates';

export interface ContractListGridProps {
  testId?: string;
}

/**
 * ContractListGrid - Displays a list of contracts for navigation and selection
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search and navigation handling
 * - Clean component structure
 */
export const ContractListGrid: React.FC<ContractListGridProps> = ({ testId }) => {
  const [showArchived, setShowArchived] = useState(false);

  const { company, handleRowSelected, handleCreateContract } = useContractNavigation();

  // Fetch contracts data
  const {
    data: contracts = [],
    isLoading,
    error,
  } = useGetAllContractsQuery(
    { company },
    {
      skip: !company,
    }
  );

  // Custom hook for search
  const { searchTerm, filteredContracts, handleSearchChange, handleClearSearch } =
    useContractSearch(contracts, showArchived);

  // Handle row selection for navigation
  const handleRowSelection = useCallback(
    (args: RowSelectEventArgs) => {
      const selectedItem = args.data as ContractView;
      if (selectedItem) {
        handleRowSelected(selectedItem);
      }
    },
    [handleRowSelected]
  );

  // Effective date template
  const effectiveDateTemplate = useCallback((rowData: ContractView) => {
    return rowData.effectiveDate
      ? new Date(rowData.effectiveDate).toLocaleDateString()
      : '-';
  }, []);

  // End date template
  const endDateTemplate = useCallback((rowData: ContractView) => {
    return rowData.endDate ? new Date(rowData.endDate).toLocaleDateString() : '-';
  }, []);

  // Grid settings
  const pageSettings = { pageSize: 100, pageSizes: [10, 25, 50, 100] };
  const filterSettings = { type: 'Excel' as FilterType };
  const sortSettings = { allowUnsort: false };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Error loading contracts. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Contracts ({filteredContracts.length}
          {searchTerm && ` of ${contracts.length}`})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateContract}
          size="small"
        >
          New Contract
        </Button>
      </Stack>

      {/* Search Row */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by payer, product, financial class, facility type..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSearchChange(e.target.value)
              }
              InputProps={{
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8fafc',
                  '&:hover': { backgroundColor: '#f1f5f9' },
                },
              }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                size="small"
              />
            }
            label="Show Archived"
            sx={{ whiteSpace: 'nowrap' }}
          />
        </Stack>
      </Box>

      {filteredContracts.length > 0 && (
        <Typography
          variant="body2"
          sx={{ color: '#64748b', fontStyle: 'italic', mb: 1, fontSize: '0.75rem' }}
        >
          Click on any row to view contract details
        </Typography>
      )}

      {filteredContracts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="textSecondary">
            {searchTerm
              ? 'No contracts match your search criteria.'
              : 'No contracts found. Click "New Contract" to create one.'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <GridComponent
            dataSource={filteredContracts}
            allowPaging={true}
            allowSorting={true}
            allowFiltering={true}
            pageSettings={pageSettings}
            filterSettings={filterSettings}
            sortSettings={sortSettings}
            rowSelected={handleRowSelection}
            height="450"
            style={{ border: 'none' }}
            data-testid={testId}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="payer"
                headerText="Payer"
                width="180"
                textAlign="Left"
                template={payerTemplate}
              />
              <ColumnDirective
                field="product"
                headerText="Product"
                width="150"
                textAlign="Left"
                template={productTemplate}
              />
              <ColumnDirective
                field="financialClass"
                headerText="Financial Class"
                width="140"
                textAlign="Center"
                template={financialClassTemplate}
              />
              <ColumnDirective
                field="calcType"
                headerText="Calc Type"
                width="120"
                textAlign="Center"
                template={calcTypeTemplate}
              />
              <ColumnDirective
                field="facilityType"
                headerText="Facility Type"
                width="130"
                textAlign="Left"
                template={facilityTypeTemplate}
              />
              <ColumnDirective
                field="effectiveDate"
                headerText="Effective Date"
                width="120"
                textAlign="Center"
                template={effectiveDateTemplate}
              />
              <ColumnDirective
                field="endDate"
                headerText="End Date"
                width="120"
                textAlign="Center"
                template={endDateTemplate}
              />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
          </GridComponent>
        </Box>
      )}
    </Box>
  );
};

export default ContractListGrid;
